import * as React from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';

const DEFAULT_DESCRIPTION = 'Hit different.';
const DEFAULT_IMAGE = `${process.env.APP_URL}/icons/social-share.png`;

interface Props {
  title?: string;
  description?: string;
  url?: string;
  ogImage?: string;
  preventBrandTitle?: boolean;
  useCurrentUrl?: boolean;
  noIndex?: boolean;
  canonicalUrl?: string;
  canonicalType?: 'currentUrl' | 'currentUrlStripQuery';
}

const addBrandTitle = (title?: string) => `${title} | Bounce`;

const getTitle = (title?: string, preventBrandTitle?: boolean) => {
  if (!title) {
    return '';
  }

  return preventBrandTitle ? title : addBrandTitle(title);
};

const getCanonicalUrl = (
  router: ReturnType<typeof useRouter>,
  canonicalUrl?: string,
  canonicalType?: 'currentUrl' | 'currentUrlStripQuery',
) => {
  if (canonicalUrl) {
    return canonicalUrl;
  }

  if (canonicalType === 'currentUrl') {
    return `${process.env.APP_URL}${router.asPath}`;
  }

  if (canonicalType === 'currentUrlStripQuery') {
    const pathname = router.pathname;
    const params = router.query;

    // Build URL with route params but without query params
    let url = pathname;
    for (const [key, value] of Object.entries(params)) {
      if (pathname.includes(`[${key}]`)) {
        url = url.replace(`[${key}]`, String(value));
      }
    }

    return `${process.env.APP_URL}${url}`;
  }

  return undefined;
};

const Head: React.FC<Props> = ({
  title,
  description,
  url,
  ogImage,
  preventBrandTitle,
  noIndex,
  canonicalUrl,
  canonicalType,
}) => {
  const router = useRouter();
  const finalTitle = getTitle(title, preventBrandTitle);
  const finalCanonicalUrl = getCanonicalUrl(router, canonicalUrl, canonicalType);
  const shouldNoIndex =
    noIndex || process.env.APP_STAGE !== 'production' || process.env.NOINDEX === 'true';
  // Prefer canonical for og:url so internal Next.js rewrite paths (e.g. /home/rewrite/anon)
  // do not leak into social/AI citation snippets.
  const finalUrl = url || finalCanonicalUrl || `${process.env.APP_URL}${router.asPath}`;

  return (
    <NextHead>
      <meta
        name="robots"
        content={shouldNoIndex ? 'noindex,follow' : 'index,follow,max-image-preview:large'}
      />

      <title>{finalTitle}</title>
      <meta name="description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={ogImage || DEFAULT_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description || DEFAULT_DESCRIPTION} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={ogImage || DEFAULT_IMAGE} />
      {finalCanonicalUrl && <link rel="canonical" href={finalCanonicalUrl} />}
    </NextHead>
  );
};

export default Head;
